import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AdminLayout } from "@/components/AdminLayout";
import { Button } from "@/components/ui/button";
import {
  Menu,
  QrCode,
  Eye,
  LifeBuoy,
  Settings,
  Layers,
  Utensils,
  Flame,
} from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const WEBMENU_BASE_URL = "https://webmenu.megriva.com";

export default function Dashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();

  const [business, setBusiness] = useState(null);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    categories: 0,
    items: 0,
    activeItems: 0,
  });

  useEffect(() => {
    const parsedUser = user || getUserFromStorage();
    const subDomain = getBusinessSubDomain(parsedUser);

    if (!subDomain) return;

    fetchData(parsedUser);
  }, [user]);

  const getUserFromStorage = () => {
    try {
      return JSON.parse(localStorage.getItem("user"));
    } catch {
      return null;
    }
  };

  const getBusinessSubDomain = (u) =>
    (
      u?.businessSubDomain ||
      u?.subDomain ||
      u?.BusinessSubDomain ||
      ""
    )
      .toString()
      .trim();

  const getBusinessId = (u) =>
    u?.businessId ?? u?.BusinessId ?? null;

  const fetchData = async (activeUser) => {
    try {
      setLoading(true);

      const parsedUser = activeUser || user;
      if (!parsedUser) return;

      const subDomain = getBusinessSubDomain(parsedUser);
      const businessId = getBusinessId(parsedUser);

      const catsRes = await api.getCategories({
        businessSubDomain: subDomain,
        page: 0,
        pageSize: 1000,
        visible: true,
      });

      const categories = catsRes?.data ?? [];

      const productResponses = await Promise.all(
        categories.map((c) =>
          api.getProducts({
            categoryId: c.id,
            page: 0,
            pageSize: 1000,
          })
        )
      );

      const allProducts = productResponses.flatMap((r) => r?.data ?? []);

      const uniqueProducts = Array.from(
        new Map(allProducts.map((p) => [p.id, p])).values()
      );

      setStats({
        categories: categories.length,
        items: uniqueProducts.length,
        activeItems: uniqueProducts.filter((p) => p.is_active).length,
      });

      if (businessId) {
        const businessRes = await api.getBusinessById(businessId);
        setBusiness(businessRes?.data ?? null);
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const StatCard = ({ title, value, icon: Icon, color }) => (
    <div className={`rounded-xl p-5 text-white shadow-md ${color}`}>
      <div className="flex justify-between items-center">
        <div>
          <div className="text-sm opacity-80">{title}</div>
          <div className="text-2xl font-bold mt-1">
            {loading ? "..." : value}
          </div>
        </div>
        <Icon className="w-6 h-6 opacity-80" />
      </div>
    </div>
  );

  const businessSubDomain =
    business?.subDomain || getBusinessSubDomain(user);

  const menuUrl = businessSubDomain
    ? `${WEBMENU_BASE_URL}/${businessSubDomain}`
    : "";

  const rawBusinessLogo =
    business?.logoUrl ||
    business?.logo_url ||
    business?.imageUrl ||
    business?.ImageUrl ||
    "";

  const businessLogo =
    typeof rawBusinessLogo === "string" &&
    /^https?:\/\//i.test(rawBusinessLogo)
      ? rawBusinessLogo
      : "";

  const mobileQuickActions = [
    {
      title: "Menü Yönetimi",
      description: "Kategori ve ürünleri düzenle",
      path: "/admin/menu",
    },
    {
      title: "QR Kodunu Al",
      description: "Menü QR kodunu görüntüle",
      path: "/admin/qr-code",
    },
    {
      title: "Menüyü Önizle",
      description: "Müşteri görünümünü kontrol et",
      path: "/admin/preview",
    },
    {
      title: "Kampanyalar",
      description: "Banner ve kampanyaları yönet",
      path: "/admin/banners",
    },
  ];

  const mobileOtherActions = [
    {
      title: "Abonelik",
      description: "Plan ve ödeme bilgilerini yönet",
      path: "/admin/subscription",
    },
    {
      title: "Ayarlar",
      description: "İşletme bilgilerini güncelle",
      path: "/admin/profile",
    },
    {
      title: "Destek",
      description: "Yardım ve destek talebi oluştur",
      path: "/admin/support",
    },
  ];

  return (
    <AdminLayout>
      {/* =====================================================
          MOBILE DASHBOARD
          WebMenuMobile DashboardScreen parity
      ====================================================== */}
      <div
        className="min-h-screen bg-[#F8F8F8] px-5 md:hidden"
        style={{
          paddingTop: "calc(env(safe-area-inset-top, 0px) + 16px)",
          paddingBottom: "calc(env(safe-area-inset-bottom, 0px) + 40px)",
        }}
      >
        {loading ? (
          <div className="flex min-h-[300px] flex-col items-center justify-center">
            <div className="h-8 w-8 animate-spin rounded-full border-[3px] border-zinc-200 border-t-[#EA580C]" />

            <div className="mt-3 text-sm text-[#71717A]">
              Veriler yükleniyor...
            </div>
          </div>
        ) : (
          <>
            {/* HERO */}
            <section className="mb-5 rounded-3xl border border-[#E4E4E7] bg-white p-5 shadow-[0_8px_24px_rgba(24,24,27,0.06)]">
              <div className="flex items-center gap-3">
                {businessLogo ? (
                  <img
                    src={businessLogo}
                    alt={business?.name || "İşletme logosu"}
                    className="h-14 w-14 shrink-0 rounded-2xl border border-[#E4E4E7] bg-[#F4F4F5] object-cover"
                  />
                ) : null}

                <div className="min-w-0 flex-1">
                  <div className="text-[10px] font-extrabold tracking-[0.8px] text-[#EA580C]">
                    İŞLETME PANELİ
                  </div>

                  <h1 className="mt-0.5 truncate text-[25px] font-extrabold leading-[30px] text-[#18181B]">
                    {business?.name || "İşletmeniz"}
                  </h1>
                </div>

                {menuUrl ? (
                  <div className="flex min-h-[30px] shrink-0 items-center gap-1.5 rounded-full bg-[#DCFCE7] px-2.5">
                    <span className="h-1.5 w-1.5 rounded-full bg-[#16A34A]" />
                    <span className="text-[10px] font-bold text-[#15803D]">
                      Hazır
                    </span>
                  </div>
                ) : null}
              </div>

              <p className="mt-4 max-w-[340px] text-[13px] leading-5 text-[#71717A]">
                Menü, QR ve kampanyalarınızı tek yerden yönetin.
              </p>
            </section>

            {/* STATS */}
            <section className="mb-7 grid grid-cols-3 gap-2">
              <div className="flex min-h-[86px] flex-col justify-center rounded-2xl border border-[#E4E4E7] bg-white px-3 py-3">
                <div className="text-[25px] font-extrabold leading-[30px] tabular-nums text-[#18181B]">
                  {stats.categories}
                </div>
                <div className="mt-1 text-[12px] font-semibold text-[#71717A]">
                  Kategori
                </div>
              </div>

              <div className="flex min-h-[86px] flex-col justify-center rounded-2xl border border-[#E4E4E7] bg-white px-3 py-3">
                <div className="text-[25px] font-extrabold leading-[30px] tabular-nums text-[#18181B]">
                  {stats.items}
                </div>
                <div className="mt-1 text-[12px] font-semibold text-[#71717A]">
                  Ürün
                </div>
              </div>

              <div className="flex min-h-[86px] flex-col justify-center rounded-2xl border border-[#EA580C] bg-[#FFF7ED] px-3 py-3">
                <div className="text-[25px] font-extrabold leading-[30px] tabular-nums text-[#EA580C]">
                  {stats.activeItems}
                </div>
                <div className="mt-1 text-[12px] font-semibold text-[#EA580C]">
                  Aktif
                </div>
              </div>
            </section>

            {/* QUICK ACTIONS */}
            <section>
              <div className="mb-3">
                <h2 className="text-xl font-extrabold leading-[26px] text-[#18181B]">
                  Hızlı İşlemler
                </h2>

                <p className="mt-1 text-[13px] leading-5 text-[#71717A]">
                  En sık kullanılan araçlar
                </p>
              </div>

              <div className="mb-7 grid grid-cols-2 gap-3">
                {mobileQuickActions.map((item) => (
                  <button
                    key={item.path}
                    type="button"
                    onClick={() => navigate(item.path)}
                    className="relative min-h-[118px] overflow-hidden rounded-2xl border border-[#E4E4E7] bg-white p-4 text-left shadow-[0_6px_18px_rgba(24,24,27,0.05)] transition active:opacity-60"
                  >
                    <span className="absolute bottom-0 left-0 top-0 w-1 bg-[#EA580C]" />

                    <div className="flex items-start gap-2">
                      <span className="flex-1 text-[15px] font-extrabold leading-5 text-[#18181B]">
                        {item.title}
                      </span>

                      <span className="-mt-1 text-2xl font-light leading-[26px] text-[#A1A1AA]">
                        ›
                      </span>
                    </div>

                    <p className="mt-2 line-clamp-2 text-[12px] leading-4 text-[#71717A]">
                      {item.description}
                    </p>
                  </button>
                ))}
              </div>
            </section>

            {/* LIVE MENU */}
            {menuUrl ? (
              <section>
                <div className="mb-2 ml-1 text-[10px] font-extrabold tracking-[0.7px] text-[#A1A1AA]">
                  CANLI MENÜ
                </div>

                <button
                  type="button"
                  onClick={() =>
                    window.open(
                      menuUrl,
                      "_blank",
                      "noopener,noreferrer"
                    )
                  }
                  className="mb-7 flex min-h-[78px] w-full items-center gap-3 rounded-2xl border border-[#E4E4E7] bg-white px-4 py-3 text-left transition active:opacity-60"
                >
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-[7px]">
                      <span className="h-[7px] w-[7px] shrink-0 rounded-full bg-[#22C55E]" />

                      <span className="text-[13px] font-bold text-[#18181B]">
                        Menü bağlantısı
                      </span>
                    </div>

                    <div className="mt-1 truncate text-[12px] text-[#71717A]">
                      {menuUrl}
                    </div>
                  </div>

                  <div className="flex min-h-[38px] min-w-[50px] items-center justify-center rounded-xl bg-[#FFF7ED] px-3">
                    <span className="text-[12px] font-extrabold text-[#EA580C]">
                      Aç
                    </span>
                  </div>
                </button>
              </section>
            ) : null}

            {/* OTHER */}
            <section>
              <div className="mb-2 ml-1 text-[10px] font-extrabold tracking-[0.7px] text-[#A1A1AA]">
                DİĞER
              </div>

              <div className="overflow-hidden rounded-2xl border border-[#E4E4E7] bg-white">
                {mobileOtherActions.map((item, index) => (
                  <div key={item.path}>
                    <button
                      type="button"
                      onClick={() => navigate(item.path)}
                      className="flex min-h-[70px] w-full items-center gap-3 px-4 py-3 text-left transition active:bg-[#F4F4F5]"
                    >
                      <div className="min-w-0 flex-1">
                        <div className="text-[14px] font-bold text-[#18181B]">
                          {item.title}
                        </div>

                        <div className="mt-1 truncate text-[12px] text-[#71717A]">
                          {item.description}
                        </div>
                      </div>

                      <span className="-mt-0.5 text-[25px] font-light leading-[27px] text-[#A1A1AA]">
                        ›
                      </span>
                    </button>

                    {index < mobileOtherActions.length - 1 ? (
                      <div className="ml-4 h-px bg-[#E4E4E7]" />
                    ) : null}
                  </div>
                ))}
              </div>
            </section>
          </>
        )}
      </div>

      {/* =====================================================
          DESKTOP DASHBOARD
          Mevcut görünüm korunuyor
      ====================================================== */}
      <div className="hidden max-w-6xl mx-auto space-y-8 md:block">

        {/* HEADER */}
        <div>
          <h1 className="text-3xl font-bold">Kontrol Paneli</h1>
          <p className="text-zinc-500 mt-2">
            Restoranınızı buradan yönetin 🚀
          </p>
        </div>

        {/* STATS */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
          <StatCard
            title="Kategoriler"
            value={stats.categories}
            icon={Layers}
            color="bg-gradient-to-r from-indigo-500 to-indigo-600"
          />
          <StatCard
            title="Toplam Menü"
            value={stats.items}
            icon={Utensils}
            color="bg-gradient-to-r from-orange-500 to-orange-600"
          />
          <StatCard
            title="Aktif Ürünler"
            value={stats.activeItems}
            icon={Flame}
            color="bg-gradient-to-r from-emerald-500 to-emerald-600"
          />
        </div>

        {/* ACTIONS */}
        <div className="rounded-2xl border border-zinc-100 bg-white p-5 md:p-6 shadow-sm">
          <div className="mb-5 flex items-center justify-between gap-3">
            <div>
              <h2 className="text-lg font-semibold text-zinc-900">Hızlı İşlemler</h2>
              <p className="mt-1 text-sm text-zinc-500">
                En çok kullanılan yönetim araçlarına hızlıca ulaşın.
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
            {[
              {
                title: "Menü Yönetimi",
                desc: "Kategori ve ürünleri düzenle",
                icon: Menu,
                path: "/admin/menu",
              },
              {
                title: "QR Kodunu Al",
                desc: "Menü QR kodunu görüntüle",
                icon: QrCode,
                path: "/admin/qr-code",
              },
              {
                title: "Menüyü Önizle",
                desc: "Müşteri görünümünü kontrol et",
                icon: Eye,
                path: "/admin/preview",
              },
              {
                title: "Kampanyalar",
                desc: "Banner ve kampanyaları yönet",
                icon: Settings,
                path: "/admin/banners",
              },
              {
                title: "Ayarlar",
                desc: "Restoran bilgilerini güncelle",
                icon: Settings,
                path: "/admin/profile",
              },
              {
                title: "Destek",
                desc: "Yardım ve destek talebi oluştur",
                icon: LifeBuoy,
                path: "/admin/support",
              },
            ].map((item) => {
              const Icon = item.icon;

              return (
                <button
                  key={item.path}
                  type="button"
                  onClick={() => navigate(item.path)}
                  className="group flex items-center gap-4 rounded-2xl border border-zinc-100 bg-zinc-50/70 p-4 text-left transition-all duration-200 hover:-translate-y-0.5 hover:border-orange-200 hover:bg-white hover:shadow-md"
                >
                  <div className="grid h-11 w-11 shrink-0 place-items-center rounded-xl bg-white text-zinc-800 shadow-sm ring-1 ring-zinc-100 transition group-hover:bg-orange-500 group-hover:text-white">
                    <Icon className="h-5 w-5" />
                  </div>

                  <div className="min-w-0">
                    <div className="font-semibold text-zinc-900">{item.title}</div>
                    <div className="mt-1 text-sm text-zinc-500">{item.desc}</div>
                  </div>
                </button>
              );
            })}
          </div>
        </div>

        {/* RESTAURANT */}
        {business && (
          <div className="overflow-hidden rounded-2xl border border-zinc-100 bg-white shadow-sm">
            <div className="relative bg-gradient-to-r from-zinc-900 via-zinc-800 to-orange-500 p-6 text-white">
              <div className="flex items-start justify-between gap-4">
                <div>
                  <div className="text-xs font-medium uppercase tracking-wider text-white/60">
                    Restoran Profili
                  </div>

                  <h2 className="mt-2 text-2xl font-bold">
                    {business.name || "Restoran"}
                  </h2>

                  <p className="mt-2 text-sm text-white/70">
                    Menü bağlantınız, iletişim bilgileriniz ve restoran detaylarınız.
                  </p>
                </div>

                <div className="grid h-12 w-12 shrink-0 place-items-center rounded-2xl bg-white/15 ring-1 ring-white/20">
                  <Utensils className="h-6 w-6" />
                </div>
              </div>
            </div>

            <div className="grid gap-4 p-5 md:grid-cols-2">
              <div className="rounded-2xl bg-zinc-50 p-4">
                <div className="text-xs text-zinc-500">Restoran Adı</div>
                <div className="mt-1 font-semibold text-zinc-900">
                  {business.name || "-"}
                </div>
              </div>

              <div className="rounded-2xl bg-zinc-50 p-4">
                <div className="text-xs text-zinc-500">Telefon</div>
                <div className="mt-1 font-semibold text-zinc-900">
                  {business.phone || "-"}
                </div>
              </div>

              <div className="rounded-2xl bg-zinc-50 p-4 md:col-span-2">
                <div className="text-xs text-zinc-500">Adres</div>
                <div className="mt-1 font-semibold text-zinc-900">
                  {business.address || "-"}
                </div>
              </div>

              {menuUrl && (
                <div className="rounded-2xl border border-orange-100 bg-orange-50 p-4 md:col-span-2">
                  <div className="text-xs text-orange-600">Menü Adresi</div>

                  <div className="mt-2 flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
                    <a
                      href={menuUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="break-all font-semibold text-orange-600 hover:text-orange-700 hover:underline"
                    >
                      {menuUrl}
                    </a>

                    <Button
                      type="button"
                      onClick={() => window.open(menuUrl, "_blank")}
                      className="rounded-full bg-zinc-900 px-5 text-white hover:bg-zinc-800"
                    >
                      Menüyü Aç
                    </Button>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </AdminLayout>
  );
}